import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface TriggerProps {
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: React.MouseEventHandler;
    onMouseLeave?: React.MouseEventHandler;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
}

interface CustomTooltipProps {
    content: React.ReactNode;
    children: React.ReactElement<TriggerProps>;
    className?: string;
    zIndex?: number;
    placement?: 'top' | 'bottom' | 'left' | 'right';
}

const BG_COLOR = 'rgba(51, 51, 51, 0.85)';

const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    backgroundColor: BG_COLOR,
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    lineHeight: '1.4',
    maxWidth: '300px',
    wordWrap: 'break-word',
    textAlign: 'center',
    pointerEvents: 'auto',
    cursor: 'pointer',
};

const ARROW_SIZE = 6;
const VIEWPORT_PADDING = 8;
const GAP = 8;

const getArrowStyle = (placement: string, arrowOffset: number): React.CSSProperties => {
    const base: React.CSSProperties = {
        position: 'absolute',
        width: 0,
        height: 0,
        borderStyle: 'solid',
    };
    switch (placement) {
        case 'top':
            return { ...base, bottom: `-${ARROW_SIZE}px`, left: `${arrowOffset}px`, transform: 'translateX(-50%)', borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`, borderColor: `${BG_COLOR} transparent transparent transparent` };
        case 'bottom':
            return { ...base, top: `-${ARROW_SIZE}px`, left: `${arrowOffset}px`, transform: 'translateX(-50%)', borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`, borderColor: `transparent transparent ${BG_COLOR} transparent` };
        case 'left':
            return { ...base, right: `-${ARROW_SIZE}px`, top: `${arrowOffset}px`, transform: 'translateY(-50%)', borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`, borderColor: `transparent transparent transparent ${BG_COLOR}` };
        case 'right':
            return { ...base, left: `-${ARROW_SIZE}px`, top: `${arrowOffset}px`, transform: 'translateY(-50%)', borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`, borderColor: `transparent ${BG_COLOR} transparent transparent` };
        default:
            return base;
    }
};

/**
 * Pure CSS/React tooltip — drop-in replacement for @tippyjs/react.
 * No external dependencies. React 19 compatible.
 * Automatically repositions to stay within the viewport.
 * Arrow always points at the trigger element.
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({ content, children, zIndex = 9999, placement = 'top' }) => {
    const [visible, setVisible] = useState(false);
    const [style, setStyle] = useState<React.CSSProperties>({});
    const [actualPlacement, setActualPlacement] = useState(placement);
    const [arrowOffset, setArrowOffset] = useState(0);
    const triggerRef = useRef<HTMLElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const show = useCallback(() => setVisible(true), []);
    const hide = useCallback(() => setVisible(false), []);

    // Hide tooltip on scroll so it doesn't float away from its trigger
    React.useEffect(() => {
        if (!visible) return;
        const onScroll = () => hide();
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [visible, hide]);

    useLayoutEffect(() => {
        if (!visible || !triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const triggerCenterX = triggerRect.left + triggerRect.width / 2;
        const triggerCenterY = triggerRect.top + triggerRect.height / 2;

        let resolvedPlacement = placement;
        if (placement === 'bottom' && triggerRect.bottom + GAP + tooltipRect.height > vh - VIEWPORT_PADDING) {
            resolvedPlacement = 'top';
        } else if (placement === 'top' && triggerRect.top - GAP - tooltipRect.height < VIEWPORT_PADDING) {
            resolvedPlacement = 'bottom';
        } else if (placement === 'right' && triggerRect.right + GAP + tooltipRect.width > vw - VIEWPORT_PADDING) {
            resolvedPlacement = 'left';
        } else if (placement === 'left' && triggerRect.left - GAP - tooltipRect.width < VIEWPORT_PADDING) {
            resolvedPlacement = 'right';
        }

        let top = 0, left = 0;
        switch (resolvedPlacement) {
            case 'top':
                top = triggerRect.top - GAP - tooltipRect.height;
                left = triggerCenterX - tooltipRect.width / 2;
                break;
            case 'bottom':
                top = triggerRect.bottom + GAP;
                left = triggerCenterX - tooltipRect.width / 2;
                break;
            case 'left':
                top = triggerCenterY - tooltipRect.height / 2;
                left = triggerRect.left - GAP - tooltipRect.width;
                break;
            case 'right':
                top = triggerCenterY - tooltipRect.height / 2;
                left = triggerRect.right + GAP;
                break;
        }

        // Clamp to viewport edges
        if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
        if (left + tooltipRect.width > vw - VIEWPORT_PADDING) left = vw - VIEWPORT_PADDING - tooltipRect.width;
        if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
        if (top + tooltipRect.height > vh - VIEWPORT_PADDING) top = vh - VIEWPORT_PADDING - tooltipRect.height;

        // Calculate arrow offset: where the trigger center falls within the tooltip
        let arrow: number;
        const minArrow = ARROW_SIZE + 4; // keep arrow away from corners
        if (resolvedPlacement === 'top' || resolvedPlacement === 'bottom') {
            arrow = triggerCenterX - left;
            arrow = Math.max(minArrow, Math.min(arrow, tooltipRect.width - minArrow));
        } else {
            arrow = triggerCenterY - top;
            arrow = Math.max(minArrow, Math.min(arrow, tooltipRect.height - minArrow));
        }

        setActualPlacement(resolvedPlacement);
        setArrowOffset(arrow);
        setStyle({ top, left });
    }, [visible, placement]);

    const child = React.cloneElement(children, {
        ref: (node: HTMLElement | null) => { triggerRef.current = node; },
        onMouseEnter: (e: React.MouseEvent) => {
            show();
            children.props.onMouseEnter?.(e);
        },
        onMouseLeave: (e: React.MouseEvent) => {
            hide();
            children.props.onMouseLeave?.(e);
        },
        onFocus: (e: React.FocusEvent) => {
            show();
            children.props.onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent) => {
            hide();
            children.props.onBlur?.(e);
        },
    });

    return (
        <>
            {child}
            {visible && createPortal(
                <div
                    ref={tooltipRef}
                    style={{ ...tooltipStyle, zIndex, ...style }}
                    onClick={(e) => { e.stopPropagation(); hide(); }}
                >
                    {content}
                    <div style={getArrowStyle(actualPlacement, arrowOffset)} />
                </div>,
                document.body
            )}
        </>
    );
};

export default CustomTooltip;
